import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { authApiClient } from '../../authApiClient';
import { apiClient } from '../../publicApiClient';
import StandaloneLanguageSwitcher from '../../components/StandaloneLanguageSwitcher';
import SEO from '../../components/SEO';
import { isNetworkError } from '../../utils/errorHandler';

const COUNTRY_CODES = [
  { code: '+48', name: 'Poland', phoneLength: 9 },
  { code: '+995', name: 'Georgia', phoneLength: 9 },
];

const JoinPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { joinCode } = useParams<{ joinCode?: string }>();
  const [searchParams] = useSearchParams();
  const tutorToken = searchParams.get('tutorToken');
  const isTutorInvite = !!tutorToken;
  const [formData, setFormData] = useState({
    username: '',
    phone: '',
    countryCode: '+48',
  });
  const [error, setError] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [spaceName, setSpaceName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedCountry = COUNTRY_CODES.find(c => c.code === formData.countryCode) || COUNTRY_CODES[0];

  useEffect(() => {
    const fetchJoinTarget = async () => {
      try {
        if (isTutorInvite && tutorToken) {
          const invitationInfo = await apiClient.getTutorInvitationInfo(tutorToken);
          setOrganizationName(invitationInfo.organizationName);
          setSpaceName(invitationInfo.spaceName ?? null);
        } else if (joinCode) {
          const joinInfo = await apiClient.getJoinInfo(joinCode);
          setOrganizationName(joinInfo.organizationName);
        } else {
          setError(t('join.invalidCode'));
        }
      } catch (err) {
        setError(t('join.invalidCode'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchJoinTarget();
  }, [joinCode, isTutorInvite, tutorToken, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const maxLength = selectedCountry.phoneLength;
      if (new RegExp(`^\\d{0,${maxLength}}$`).test(value)) {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        setError('');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleCountryCodeChange = (e: any) => {
    const newCountryCode = e.target.value;
    setFormData(prev => ({
      ...prev,
      countryCode: newCountryCode,
      phone: '',
    }));
    setError('');
  };

  const getErrorMessage = (errorMessage: string): string => {
    if (errorMessage.includes('User not found')) {
      return t('auth.userNotFound');
    }
    if (errorMessage.includes('Invalid credentials')) {
      return t('auth.invalidCredentials');
    }
    if (errorMessage.includes('User account not confirmed')) {
      return t('auth.userNotConfirmed');
    }
    if (errorMessage.includes('Invalid OTP code')) {
      return t('auth.invalidOtpCode');
    }
    if (errorMessage.includes('OTP code has expired')) {
      return t('auth.otpCodeExpired');
    }
    if (errorMessage.includes('Invalid or expired refresh token')) {
      return t('auth.invalidRefreshToken');
    }
    
    if (isNetworkError(errorMessage)) {
      return t('auth.networkError');
    }
    
    if (errorMessage.includes('Internal server error') || errorMessage.includes('500')) {
      return t('auth.serverError');
    }
    
    return errorMessage || t('auth.unknownError');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    if (formData.phone.length !== selectedCountry.phoneLength) {
      setError(t('auth.phoneNumberLengthError'));
      setIsSubmitting(false);
      return;
    }

    try {
      const fullPhoneNumber = `${formData.countryCode}${formData.phone}`;
      await authApiClient.initiateLogin({ phoneNumber: fullPhoneNumber });
      navigate('/verify-code', { 
        state: { 
          phone: fullPhoneNumber,
          verificationType: isTutorInvite ? 'joinTutor' : 'joinOrganization',
          registrationData: isTutorInvite
            ? {
                name: formData.username,
                tutorToken: tutorToken!,
              }
            : {
                name: formData.username,
                joinCode: joinCode!,
              },
        } 
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(getErrorMessage(errorMessage));
      console.error('Join error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitLabel = isTutorInvite
    ? t('join.joinAsTutor', 'Join as tutor')
    : t('join.joinOrganization', 'Join Organization');

  const pageTitle = isTutorInvite
    ? t('join.tutorTitle', 'Join as tutor')
    : t('join.title', 'Join Organization');

  if (isLoading) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography>{t('common.loading')}</Typography>
        </Box>
      </Container>
    );
  }

  if (error && !organizationName) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/')}
          >
            {t('common.back', 'Back')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <SEO 
        title={pageTitle}
        noindex={true}
      />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
            <StandaloneLanguageSwitcher />
          </Box>
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              {pageTitle}
            </Typography>
            
            <Typography variant="body2" align="center" sx={{ mb: 3 }}>
              {isTutorInvite ? (
                <>
                  {t('join.joinTutorInvite', 'You were invited to teach at')}{' '}
                  <strong>{organizationName}</strong>
                </>
              ) : (
                <>
                  {t('join.joinOrganization', 'Join Organization')}: <strong>{organizationName}</strong>
                </>
              )}
            </Typography>

            {isTutorInvite && spaceName && (
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
                {t('join.attachedSpace', 'Attached space')}: <strong>{spaceName}</strong>
              </Typography>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label={t('signup.username', 'Username')}
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    autoComplete="username"
                    disabled={isSubmitting}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <Select
                          value={formData.countryCode}
                          onChange={handleCountryCodeChange}
                          disabled={isSubmitting}
                          sx={{ height: '56px' }}
                        >
                          {COUNTRY_CODES.map((country) => (
                            <MenuItem key={country.code} value={country.code}>
                              {country.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        required
                        fullWidth
                        label={t('auth.phoneNumber', 'Phone Number')}
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                        error={!!error}
                        helperText={t('auth.phoneHelp', `Enter ${selectedCountry.phoneLength} digits after ${formData.countryCode}`)}
                        disabled={isSubmitting}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              
              {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting}
                sx={{ mt: 3, mb: 2 }}
              >
                {isSubmitting ? t('common.loading', 'Loading...') : submitLabel}
              </Button>
              
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/login')}
                disabled={isSubmitting}
              >
                {t('auth.haveAccount', 'Already have an account? Sign in')}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default JoinPage;
